import * as Dialog from '@radix-ui/react-dialog'
import { Link } from '@tanstack/react-router'
import { FileText, X } from 'lucide-react'
import { DistributionSummary } from '@/components/invoices/DistributionSummary'
import { InvoiceStatusPill } from '@/components/invoices/InvoiceStatusPill'
import { InvoiceTimeline } from '@/components/invoices/InvoiceTimeline'
import { LifecycleStepper } from '@/components/invoices/LifecycleStepper'
import { OpenDurationBadge } from '@/components/invoices/OpenDurationBadge'
import { Money } from '@/components/ui/Money'
import { Skeleton } from '@/components/ui/Skeleton'
import { toNumber } from '@/lib/decimal'
import { useInvoice } from '@/lib/invoice-queries'

export function InvoicePanel({
  invoiceId,
  open,
  onOpenChange,
}: {
  invoiceId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-(--scrim) backdrop-blur-xs animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-50 flex w-[480px] max-w-[100vw] flex-col bg-surface shadow-pop animate-slide-in-right focus:outline-hidden"
        >
          {invoiceId ? <PanelBody invoiceId={invoiceId} onClose={() => onOpenChange(false)} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function PanelBody({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const invoiceQuery = useInvoice(invoiceId)

  if (invoiceQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Dialog.Close className="btn-ghost h-8 w-8 shrink-0 p-0" aria-label="Sluiten">
            <X className="h-4 w-4" />
          </Dialog.Close>
        </div>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-end border-b border-hairline px-5 py-4">
          <Dialog.Close className="btn-ghost h-8 w-8 shrink-0 p-0" aria-label="Sluiten">
            <X className="h-4 w-4" />
          </Dialog.Close>
        </div>
        <p className="p-5 text-sm text-ink-faint">Deze factuur kon niet worden geladen.</p>
      </div>
    )
  }

  const invoice = invoiceQuery.data
  const meta = [
    invoice.issueDate ? `Factuurdatum ${invoice.issueDate}` : null,
    invoice.dueDate ? `Vervalt ${invoice.dueDate}` : null,
    invoice.periodLabel ? `Periode ${invoice.periodLabel}` : null,
  ].filter(Boolean)

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 border-b border-hairline px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <Dialog.Title className="num text-base font-semibold text-ink">
              {invoice.fullNumber ?? 'concept'}
            </Dialog.Title>
            <InvoiceStatusPill status={invoice.status} detail={invoice} />
            <OpenDurationBadge daysOpen={invoice.daysOpen} needsAttention={invoice.needsAttention} />
          </div>
          <p className="mt-1 truncate text-sm text-ink-faint">
            {invoice.clientName} · {invoice.subject}
          </p>
        </div>
        <Dialog.Close className="btn-ghost h-8 w-8 shrink-0 p-0" aria-label="Sluiten">
          <X className="h-4 w-4" />
        </Dialog.Close>
      </div>

      <div className="space-y-5 border-b border-hairline px-5 py-4">
        <div>
          <div className="flex justify-end">
            <Money
              amount={toNumber(invoice.subtotal)}
              secondaryAmount={toNumber(invoice.total)}
              className="text-[24px] font-semibold text-ink"
            />
          </div>
          {meta.length > 0 ? <p className="mt-0.5 text-right text-xs text-ink-faint">{meta.join(' · ')}</p> : null}
          <DistributionSummary invoice={invoice} onNavigate={onClose} />
        </div>

        <LifecycleStepper status={invoice.status} reason={invoice.closureReason} />

        <div className="flex gap-2">
          <a
            href={`/api/invoices/${invoice.id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary flex-1"
          >
            <FileText className="h-4 w-4" />
            Open PDF
          </a>
          <Link
            to="/facturen/$id"
            params={{ id: invoice.id }}
            onClick={onClose}
            className="btn-secondary flex-1"
          >
            Detailpagina
          </Link>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <p className="label-caps mb-3">Activiteit</p>
        <InvoiceTimeline events={invoice.events} comments={invoice.comments} />
      </div>
    </div>
  )
}
